import { format } from "date-fns";
import Decimal from "decimal.js";
import { parseDisplayDate, formatDisplayDate } from "./safeDate";

class ConstantFunctions {

  public formatDateAsddmmYY(dateString: string) {
    const date = parseDisplayDate(dateString);
    if (!date) return dateString || "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-indexed
    const year = String(date.getFullYear()).slice(-2); // get last 2 digits

    return `${day}/${month}/${year}`;
  }

  public toSentenceCase(str: string) {
    if (!str) return "";
    str = str.trim().toLowerCase();
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  public dateFormatInDDMMM(dateString: string | null = ""): string {
    const date = parseDisplayDate(dateString);
    if (!date) return "";
    return format(date, "dd MMM");
  }

  public dateFormatInDDMMYY(dateString: string | null = ""): string {
    const date = parseDisplayDate(dateString);
    if (!date) return "";
    return format(date, "dd/MM/yyyy");
  }

  public formatDate(inputDate: string): string {
    if (!inputDate) return "";
    return formatDisplayDate(inputDate, "en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  public formatDateTime(inputDate: string): string {
    if (!inputDate) return "";
    return formatDisplayDate(inputDate, "en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  }

  public timeFormatInHHMMSS(dateString: string | null = ""): string {
    const date = parseDisplayDate(dateString);
    if (!date) return "";
    return format(date, "HH:mm:ss");
  }

  public getHashAddressText(address: string | null = ""): string {
    if (!address) {
      return ""; // Handle null or empty string
    } else {
      const addressLength = address.length;
      return (
        address.substring(0, 4) +
        "..." +
        address.substring(addressLength - 4, addressLength)
      );
    }
  }

  public formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  public formatNumber = (value: any) => {
    if (value === null || value === undefined) return "";
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toLocaleString("en-US");
  };

  public formatRevenue = (value: number) => {
    if (isNaN(value)) {
      return "$0.00";
    }
    return (
      "$" +
      new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    );
  };

  public formatAmount(amount: string | number): string {
    return new Decimal(amount || 0)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toNumber()
      .toFixed(2);
  }

  public maskName = (value: string): string => {
    if (!value) return "";

    // Remove spaces
    const cleanValue = value.replace(/\s+/g, "");

    if (cleanValue.length <= 3) return cleanValue;

    const firstTwo = cleanValue.slice(0, 1);
    const lastOne = cleanValue.slice(-1);

    return `${firstTwo}***${lastOne}`;
  };

  public getTimeDifferenceString = (
    createdAt: string,
    updatedAt: string,
  ): string => {
    const createdDate = parseDisplayDate(createdAt);
    const updatedDate = parseDisplayDate(updatedAt);
    if (!createdDate || !updatedDate) return "";

    const differenceInMs = updatedDate.getTime() - createdDate.getTime();

    // Convert milliseconds to seconds, minutes, hours
    const differenceInSeconds = Math.floor(differenceInMs / 1000);
    const differenceInMinutes = Math.floor(differenceInSeconds / 60);
    const differenceInHours = Math.floor(differenceInMinutes / 60);

    // Return appropriate string based on the time difference
    if (differenceInSeconds < 60) {
      return `${differenceInSeconds} Sec`;
    } else if (differenceInMinutes < 60) {
      return `${differenceInMinutes} Min${differenceInMinutes > 1 ? "s" : ""}`;
    } else {
      return `${differenceInHours} Hour${differenceInHours > 1 ? "s" : ""}`;
    }
  };
}

export default ConstantFunctions;
